'use client';

import { c as _c } from "react/compiler-runtime";
import { Select } from '@/features/payload-cms/components/form/select';
import type { HofSelectionBlock } from '@/features/payload-cms/components/form/types';
import { useCurrentLocale } from '@/hooks/use-current-locale';
import { trpc } from '@/trpc/client';
import type { StaticTranslationString } from '@/types/types';
import { i18nConfig } from '@/types/types';
import type React from 'react';
import type { Control, FieldError, FieldErrorsImpl, FieldValues, Merge, UseFormRegister } from 'react-hook-form';
const placeholderText: StaticTranslationString = {
  de: 'Hof auswählen',
  en: 'Select a Hof',
  fr: 'Choisir un Hof'
};
const loadErrorText: StaticTranslationString = {
  de: 'Die Höfe konnten nicht geladen werden. Prüfe deine Verbindung.',
  en: 'The Höfe could not be loaded. Check your connection.',
  fr: 'Les Hofs n’ont pas pu être chargés. Vérifie ta connexion.'
};
const loadingText: StaticTranslationString = {
  de: 'Höfe werden geladen …',
  en: 'Loading Höfe …',
  fr: 'Chargement des Hofs …'
};

/**
 * Lets the person filling in a form pick their Hof. The answer is the Hof's id; the submission
 * hook links the submission to that Hof and stores its name as the answer.
 */
export const HofSelection: React.FC<HofSelectionBlock & {
  control: Control;
  registerAction: UseFormRegister<string & FieldValues>;
  error?: FieldError | Merge<FieldError, FieldErrorsImpl<FieldValues>>;
}> = t0 => {
  const $ = _c(16);
  const {
    name,
    label,
    required,
    control,
    registerAction,
    error
  } = t0;
  const locale = useCurrentLocale(i18nConfig) as keyof StaticTranslationString;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      refetchOnMount: true
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    data: hoefe,
    isLoading,
    isError,
    fetchStatus
  } = trpc.hofDashboard.getHofList.useQuery(undefined, t1);
  const unavailable = hoefe === undefined && (isError || fetchStatus === "paused");
  const t2 = label ?? "";
  const t3 = required ?? false;
  let t4;
  if ($[1] !== error) {
    t4 = error === undefined ? {} : {
      error
    };
    $[1] = error;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const t5 = unavailable ? loadErrorText[locale] : (isLoading ? loadingText : placeholderText)[locale];
  let t6;
  if ($[3] !== hoefe) {
    t6 = hoefe ?? [];
    $[3] = hoefe;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  let t7;
  if ($[5] !== t6) {
    t7 = t6.map(_temp);
    $[5] = t6;
    $[6] = t7;
  } else {
    t7 = $[6];
  }
  let t8;
  if ($[7] !== control || $[8] !== name || $[9] !== registerAction || $[10] !== t2 || $[11] !== t3 || $[12] !== t4 || $[13] !== t5 || $[14] !== t7) {
    t8 = <Select blockType="select" name={name} label={t2} required={t3} control={control} registerAction={registerAction} {...t4} optionType="dropdown" allowMultiple={false} placeholder={t5} options={t7} />;
    $[7] = control;
    $[8] = name;
    $[9] = registerAction;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  return t8;
};
function _temp(hof) {
  return {
    value: hof.id,
    label: hof.name
  };
}